import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { UserType } from "../../types/user";
import type { RootState } from "../../types/store";
import { ApolloClientType } from "../../types/apollo";
import { ADD_MESSAGE, GET_MESSAGES } from "../../queries/chat.qql";
import { ChatDataType } from "../../types/chat";
import { logOut } from "./user";

const initialState: ChatDataType = {
  messages: [],
  loading: false,
  sendLoading: false,
  contact: null,
};

export const fetchMessages = createAsyncThunk(
  "messages/fetch",
  async (_, { getState, extra }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };
    const { user, chat } = getState() as RootState;
    const { id, role } = user as UserType;

    const isDoctor = role === "oncologist" || role === "nutrizionist";

    const { data } = await apolloClient.mutate({
      mutation: GET_MESSAGES,
      variables: {
        // @ts-ignore
        professionistId: isDoctor ? +id : +chat.contact,
        // @ts-ignore
        patientId: isDoctor ? +chat.contact : +id
      },
      fetchPolicy: "no-cache"
    });

    return data.messages;
  },
);

export const addMessage = createAsyncThunk(
  "messages/add",
  async (content: string, { getState, extra }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };
    const { user, chat } = getState() as RootState;
    const { id, role } = user as UserType;

    const isDoctor = role === "oncologist" || role === "nutrizionist";

    const { data } = await apolloClient.query({
      query: ADD_MESSAGE,
      variables: {
        from: +id,
        // @ts-ignore
        professionistId: isDoctor ? +id : +chat.contact,
        // @ts-ignore
        patientId: isDoctor ? +chat.contact : +id,
        content
      },
      fetchPolicy: "no-cache"
    });

    return data.addMessage;
  },
);

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setContact(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      state.contact = id;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchMessages.fulfilled, (state, action) => {
      state.messages = action.payload;
      state.loading = false;
    });
    builder.addCase(addMessage.pending, (state) => {
      state.sendLoading = true;
    });
    builder.addCase(addMessage.fulfilled, (state, action) => {
      state.sendLoading = false;
      state.messages.push(action.payload);
    });
    builder.addCase(logOut.fulfilled, (state) => {
      state.messages = [];
      state.loading = false;
      state.sendLoading = false;
      state.contact = null;
    });
  },
});

export const {
  setContact
} = chatSlice.actions;

export const getMessages = (state: RootState) => state.chat.messages;
export const chatLoading = (state: RootState) => state.chat.loading;
export const sendLoading = (state: RootState) => state.chat.sendLoading;
export const chatContact = (state: RootState) => state.chat.contact;
